export type ErrorType =
  | "validation"
  | "conflict"
  | "notfound"
  | "forbidden"
  | "unauthorized"
  | "server"
  | "unknown";

export interface FormattedError {
  message: string;
  type: ErrorType;
  showSupportMessage: boolean;
  hasSpecificErrors?: boolean;
}

/**
 * Check if error message is user-friendly (not technical)
 */
function isUserFriendlyMessage(message: string): boolean {
  // Technical messages to filter out
  const technicalPatterns = [
    "System.",
    "JSON value could not be converted",
    "BytePositionInLine",
    "LineNumber",
    "Path:",
    "could not be converted to",
    "Nullable",
    "expected",
    "serialization",
    "deserialization",
  ];

  return !technicalPatterns.some((pattern) => message.includes(pattern));
}

/**
 * Extract and format specific validation error messages from API response
 * Returns formatted error message with field-specific details
 */
function extractValidationErrors(errorData: any): {
  message: string | null;
  hasSpecificErrors: boolean;
} {
  if (!errorData) return { message: null, hasSpecificErrors: false };

  const userFriendlyErrors: string[] = [];
  const technicalErrors: string[] = [];
  let hasAnyErrors = false;

  // Check for errors object (most common format)
  if (errorData.errors && typeof errorData.errors === "object") {
    Object.entries(errorData.errors).forEach(
      ([field, messages]: [string, any]) => {
        if (Array.isArray(messages)) {
          messages.forEach((msg: string) => {
            hasAnyErrors = true;
            const cleanField = cleanFieldName(field);
            const cleanMsg = msg.trim();

            // Check if message is user-friendly
            if (isUserFriendlyMessage(cleanMsg)) {
              // Show field + message for user-friendly errors
              userFriendlyErrors.push(`${cleanField}: ${cleanMsg}`);
            } else {
              // Track technical errors
              technicalErrors.push(`${cleanField}`);
            }
          });
        }
      },
    );
  }

  // If we have user-friendly errors, show them
  if (userFriendlyErrors.length > 0) {
    return {
      message: userFriendlyErrors.join("\n"),
      hasSpecificErrors: true,
    };
  }

  // If we only have technical errors, show which fields are problematic
  if (technicalErrors.length > 0) {
    const uniqueFields = Array.from(new Set(technicalErrors));
    return {
      message: `Please check the following fields:\n${uniqueFields.join(", ")}`,
      hasSpecificErrors: true,
    };
  }

  // Check for detail field that might contain errors
  if (errorData.detail && typeof errorData.detail === "string") {
    try {
      const parsed = JSON.parse(errorData.detail);
      if (parsed?.errors && typeof parsed.errors === "object") {
        return extractValidationErrors(parsed);
      }
    } catch (e) {
      // If not JSON, use the detail string if it's user-friendly
      if (
        errorData.detail &&
        !errorData.detail.includes("System.") &&
        errorData.detail.length < 200
      ) {
        return {
          message: errorData.detail,
          hasSpecificErrors: true,
        };
      }
    }
  }

  return { message: null, hasSpecificErrors: false };
}

/**
 * Clean up field names from API paths to user-friendly format
 * E.g., "$.claimRequest.insuranceProviderId" -> "Insurance Provider Id"
 *       "updateRequest" -> "Update Request"
 */
function cleanFieldName(field: string): string {
  // Remove path notation: $.claimRequest.insuranceProviderId -> insuranceProviderId
  let name =
    field
      .replace(/^\$\./, "") // Remove leading $.
      .replace(/\[\d+\]\.?/g, "") // Remove array indices
      .split(".") // Split by dot
      .pop() || field; // Get last part

  // Convert camelCase to Title Case
  // insuranceProviderId -> Insurance Provider Id
  name = name
    .replace(/([A-Z])/g, " $1") // Add space before capitals
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();

  return name;
}

export function formatApiError(errorData: any, status: number): FormattedError {
  let errorType: ErrorType = "unknown";
  let message = "";
  let showSupportMessage = false;
  let hasSpecificErrors = false;

  if (status === 400) {
    errorType = "validation";

    const { message: extractedMessage, hasSpecificErrors: hasErrors } =
      extractValidationErrors(errorData);

    if (extractedMessage && hasErrors) {
      message = extractedMessage;
      hasSpecificErrors = true;
    } else {
      message =
        "Something is missing or incorrect. Please check all the details and try again.";
    }
    showSupportMessage = true;
  } else if (status === 409) {
    errorType = "conflict";
    // Use the detail message if available for conflict errors (usually more specific)
    if (typeof errorData === "string") {
      message = errorData;
    } else if (errorData?.detail) {
      message = errorData.detail;
    } else {
      message =
        "This resource already exists. Please try with different information.";
    }
  } else if (status === 401) {
    errorType = "unauthorized";
    message = "Your session has expired. Please sign in again to continue.";
  } else if (status === 403) {
    errorType = "forbidden";
    message = "You don't have permission to perform this action.";
  } else if (status === 404) {
    errorType = "notfound";
    message = "The requested resource was not found.";
  } else if (status === 500 || status === 502) {
    errorType = "server";
    message = "A server error occurred. Please try again later.";
    showSupportMessage = true;
  } else if (status === 429) {
    errorType = "conflict";
    if (typeof errorData === "string") {
      message = errorData;
    } else if (errorData?.detail) {
      message = errorData.detail;
    } else {
      message = "Please remove the existing one before adding a new one.";
    }
  } else {
    message =
      typeof errorData === "string"
        ? errorData
        : "An error occurred. Please try again.";
  }

  return {
    message,
    type: errorType,
    showSupportMessage,
    hasSpecificErrors,
  };
}

export function getErrorDisplayMessage(error: FormattedError): string {
  let displayMessage = error.message;

  if (error.showSupportMessage) {
    displayMessage +=
      "\n\nIf the issue persists, please contact our support team.";
  }

  return displayMessage;
}
